import React, {FC, SyntheticEvent, useCallback, useRef} from 'react';
import styled, {css} from 'styled-components';

import NoCSVImgSrc from '../../../../../../../../src/assets/images/noCSV.svg';
import {AttachmentModel} from '../../../../../../../../src/models/api/attachmentModel';
import {Plural, Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {DragAndDropAttachment} from './dragAndDropAttachment';
import {DragAndDropFileTypesEnum, DragAndDropFileTypesToAccept} from './dragAndDropConstants';

/*
 * Props.
 */

export interface DragAndDropInnerV2Props {
  inputKey: number;
  isFileHovering: boolean;
  allowedFileTypes?: ReadonlyArray<DragAndDropFileTypesEnum>;
  attachment?: AttachmentModel;
  hasError?: boolean;
  isUploading?: boolean;
  onChange: HandlerOf<SyntheticEvent<HTMLInputElement>>;
  onClearFile: Handler;
}

/*
 * Style.
 */

interface StyledWrapperDivProps {
  $isFileHovering: boolean;
  $isUploading: boolean;
  $hasError: boolean;
}

const StyledWrapperDiv = styled.div<StyledWrapperDivProps>`
  display: flex;
  flex-flow: column;
  align-items: center;
  border-radius: 16px;
  border: 2px dashed ${(p) => p.theme.greys.shade40};
  padding: 24px 0 12px 0;

  ${(p) =>
    p.$isUploading &&
    css`
      border: 2px solid ${p.theme.greys.shade10};
      background: ${p.theme.palette.grey.shade10};
    `}

  ${(p) =>
    p.$hasError &&
    css`
      border-color: ${p.theme.palette.red.shade40};
    `}

  ${(p) =>
    p.$isFileHovering &&
    css`
      border-color: ${p.theme.palette.blue.shade40};
      background: ${p.theme.palette.blue.shade10};
    `}
`;

const StyledInfoTextDiv = styled.div`
  margin-top: 16px;
  font-size: 14px;
  color: ${(p) => p.theme.greys.shade70};
  margin-bottom: 4px;
`;

const StyledFileLabel = styled.label`
  display: block;

  input {
    display: none;
  }
`;

const StyledAttachmentWrapperDiv = styled.div`
  margin: 11px 0;
  display: flex;
`;

/*
 * Component.
 */

export const DragAndDropInnerV2: FC<DragAndDropInnerV2Props> = ({
  isFileHovering,
  isUploading,
  inputKey,
  allowedFileTypes,
  attachment,
  hasError,
  onChange,
  onClearFile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onAddFileClick = useCallback(() => {
    if (!fileInputRef.current || isUploading) {
      return;
    }
    fileInputRef.current.click();
  }, [isUploading]);

  const accept = allowedFileTypes
    ? allowedFileTypes.map((val) => DragAndDropFileTypesToAccept[val]).join(',')
    : '*';

  return (
    <StyledWrapperDiv
      $isFileHovering={isFileHovering}
      $isUploading={Boolean(isUploading)}
      $hasError={Boolean(hasError)}
    >
      {attachment !== undefined && !hasError ? (
        <StyledAttachmentWrapperDiv>
          <DragAndDropAttachment
            attachment={attachment}
            hasError={hasError}
            canBeRemoved
            onRemoveClick={onClearFile}
          />
        </StyledAttachmentWrapperDiv>
      ) : (
        <img src={NoCSVImgSrc} alt="no-csv" />
      )}
      <StyledInfoTextDiv>{renderInfoText(allowedFileTypes)}</StyledInfoTextDiv>
      <Button variant={ButtonVariantsEnum.LINK} isDisabled={isUploading} onClick={onAddFileClick}>
        <Plural
          id="drag-and-drop-choose-file-button"
          value={allowedFileTypes ? allowedFileTypes.length : 0}
          one="Choose file"
          other="Choose files"
        />
      </Button>
      <StyledFileLabel>
        <input ref={fileInputRef} key={inputKey} type="file" accept={accept} onChange={onChange} />
      </StyledFileLabel>
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderInfoText(allowedFileTypes?: ReadonlyArray<DragAndDropFileTypesEnum>) {
  if (!allowedFileTypes) {
    return <Trans id="drag-and-drop-info-text-file">Drag and drop your file or</Trans>;
  }

  if (allowedFileTypes.length > 1) {
    return <Trans id="drag-and-drop-info-text-files">Drag and drop your files or</Trans>;
  }

  if (allowedFileTypes[0] === DragAndDropFileTypesEnum.CSV) {
    return <Trans id="drag-and-drop-info-text-csv">Drag and drop your CSV or</Trans>;
  }

  return <Trans id="drag-and-drop-info-text-json">Drag and drop your JSON file or</Trans>;
}
